// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Box,
  Grid,
  TextField,
  Button,
  Icon,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { DateUtils, StringUtils } from '../../../../../../common/utils';
import { useDialog } from '../../../../../../common/hooks';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    flex: '1',
    display: 'flex',
    padding: theme.spacing(4, 3),
  },
  panel: {
    flex: '1',
    marginRight: theme.spacing(3),
    '&:last-child': {
      marginRight: '0',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
  checkbox: {
    marginTop: theme.spacing(1),
  },
}));

function ContractDetailsTab(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const { contract, onCloseContract, onGoBack, actionsDisabled } = props;

  const handleShowCloseContractDialog = () => {
    showDialog({
      title: t('common.important'),
      content: params => (
        <>
          <Typography>{t('contract.contractCloseConfirm')}</Typography>
          <FormControlLabel
            className={classes.checkbox}
            control={
              <Checkbox
                id="ContractDetailsTab-forceClose"
                color="primary"
                onChange={event => params.setValue(event.target.checked)}
              />
            }
            label={t('contract.contractCloseForce')}
          />
        </>
      ),
      onConfirm: onCloseContract,
    });
  };

  const isCancellable = () => {
    return (
      actionsDisabled ||
      (!_.isEqual(contract.status, 'Aktiv') && !_.isEqual(contract.status, 'Ikke gennemført'))
    );
  };

  return (
    <Box id="ContractDetailsTab-container" className={classes.container}>
      <Box className={classes.content}>
        <Box className={classes.panel}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-contractId"
                label={t('contract.contractId')}
                variant="outlined"
                defaultValue={contract.id}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-customerAccount"
                label={t('contract.contractCustomerAccount')}
                variant="outlined"
                defaultValue={contract.customerAccount}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-invoiceAccount"
                label={t('contract.contractInvoiceAccount')}
                variant="outlined"
                defaultValue={contract.invoiceAccount}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-productName"
                label={t('contract.contractProductName')}
                variant="outlined"
                defaultValue={contract.productName}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-unitPrice"
                label={t('contract.contractUnitPrice')}
                variant="outlined"
                defaultValue={StringUtils.getFormattedSum(contract.unitPrice)}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-status"
                label={t('contract.contractStatus')}
                variant="outlined"
                defaultValue={contract.status}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.panel}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-created"
                label={t('contract.contractCreated')}
                variant="outlined"
                defaultValue={contract.createdAt && DateUtils.getDateTime(contract.createdAt)}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-updated"
                label={t('contract.contractUpdated')}
                variant="outlined"
                defaultValue={contract.updatedAt && DateUtils.getDateTime(contract.updatedAt)}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-renewal"
                label={t('contract.contractRenewal')}
                variant="outlined"
                defaultValue={contract.renewAt && DateUtils.getDateTime(contract.renewAt)}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="ContractDetailsTab-expired"
                label={t('contract.contractExpired')}
                variant="outlined"
                defaultValue={contract.expiredOn && DateUtils.getDateTime(contract.expiredOn)}
                InputLabelProps={{ shrink: true }}
                disabled
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box className={classes.actions}>
        <Button
          id="ContractDetailsTab-back"
          className={classes.button}
          startIcon={<Icon>chevron_left</Icon>}
          onClick={onGoBack}
          disabled={actionsDisabled}
        >
          <Typography variant="button" noWrap>
            {t('common.back')}
          </Typography>
        </Button>
        <Button
          id="ContractDetailsTab-close"
          color="primary"
          className={classes.button}
          startIcon={<Icon>cancel_presentation</Icon>}
          onClick={handleShowCloseContractDialog}
          disabled={isCancellable()}
        >
          <Typography variant="button" noWrap>
            {t('contract.contractClose')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
}

ContractDetailsTab.propTypes = {
  contract: PropTypes.shape({
    id: PropTypes.number,
    companyId: PropTypes.number,
    productId: PropTypes.number,
    productName: PropTypes.string,
    customerAccount: PropTypes.string,
    invoiceAccount: PropTypes.string,
    duration: PropTypes.number,
    unitPrice: PropTypes.number,
    status: PropTypes.string,
    notes: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    expiredOn: PropTypes.string,
    renewAt: PropTypes.string,
    subscriptions: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onCloseContract: PropTypes.func.isRequired,
  onGoBack: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
};

export default ContractDetailsTab;
